import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  RefreshCw, Download, Mail, Phone, MapPin, Calendar, 
  LogOut, Trash2, MessageSquare, Tag, Clock, CheckCircle2,
  Circle, PhoneCall, CalendarCheck, Archive, Filter, X, Save, Star, Search
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-800', icon: Circle },
  contacted: { label: 'Contacted', color: 'bg-yellow-100 text-yellow-800', icon: PhoneCall },
  scheduled: { label: 'Scheduled', color: 'bg-purple-100 text-purple-800', icon: CalendarCheck },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-600', icon: Archive }
};

const REVIEW_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' }
};

const TAG_OPTIONS = [
  'Hot Lead', 'Follow Up', 'Referral', 'Assessment Booked', 
  'Remodeling Interest', 'Maintenance Only', 'Large Project'
];

const AdminPage = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeTab, setActiveTab] = useState('leads'); // 'leads' or 'reviews'
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCard, setExpandedCard] = useState(null);
  const [editingNotes, setEditingNotes] = useState({});

  // Check authentication on mount
  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('adminAuthenticated');
    if (!isAuthenticated) {
      navigate('/admin-login');
    }
  }, [navigate]);

  const fetchSubmissions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      setSubmissions(response.data.sort((a, b) => 
        new Date(b.submittedAt) - new Date(a.submittedAt)
      ));
      setLastRefresh(new Date());
      toast.success('Submissions refreshed');
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/admin`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    }
  };

  useEffect(() => {
    fetchSubmissions();
    fetchReviews();
  }, []);

  const handleReviewStatus = async (reviewId, status) => {
    try {
      await axios.patch(`${API}/reviews/${reviewId}`, { status });
      toast.success(`Review ${status}`);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const exportToCSV = () => {
    if (submissions.length === 0) {
      toast.error('No submissions to export');
      return;
    }

    const headers = ['Date', 'Name', 'Email', 'Phone', 'Location', 'Service Type', 'Ownership Length', 'Interests', 'Status', 'Tags', 'Notes', 'Message'];
    const rows = submissions.map(sub => [
      formatDate(sub.submittedAt),
      sub.name,
      sub.email,
      sub.phone,
      sub.location,
      sub.serviceType || 'N/A',
      sub.ownershipLength || 'N/A',
      (sub.interests || []).join('; '),
      sub.status || 'new',
      (sub.tags || []).join('; '),
      sub.notes || '',
      sub.message
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `valenrock-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Leads exported to CSV');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    toast.success('Logged out successfully');
    navigate('/admin-login');
  };

  const handleDelete = async (submissionId, name) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the submission from ${name}?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      const response = await axios.delete(`${API}/contact/${submissionId}`);
      
      if (response.data.success) {
        setSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
        toast.success('Submission deleted');
      } else {
        toast.error('Failed to delete submission');
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const updateSubmission = async (submissionId, updates) => {
    try {
      const response = await axios.patch(`${API}/contact/${submissionId}`, updates);
      
      if (response.data.success) {
        setSubmissions(prev => prev.map(sub => 
          sub.id === submissionId ? { ...sub, ...updates } : sub
        ));
        toast.success('Updated successfully');
      } else {
        toast.error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating submission:', error);
      toast.error('Failed to update');
    }
  };

  const handleStatusChange = (submissionId, newStatus) => {
    updateSubmission(submissionId, { status: newStatus });
  };

  const handleTagToggle = (submissionId, tag, currentTags = []) => {
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    updateSubmission(submissionId, { tags: newTags });
  };

  const handleNotesChange = (submissionId, notes) => {
    setEditingNotes(prev => ({ ...prev, [submissionId]: notes }));
  };

  const saveNotes = (submissionId) => {
    const notes = editingNotes[submissionId];
    if (notes !== undefined) {
      updateSubmission(submissionId, { notes });
      setEditingNotes(prev => {
        const newState = { ...prev };
        delete newState[submissionId];
        return newState;
      });
    }
  };

  // Filter submissions
  const filteredSubmissions = submissions.filter(sub => {
    const matchesStatus = filterStatus === 'all' || (sub.status || 'new') === filterStatus;
    const matchesSearch = !searchQuery || 
      sub.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.phone?.includes(searchQuery) ||
      sub.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.message?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Average rating
  const approvedReviews = reviews.filter(r => r.status === 'approved');
  const averageRating = approvedReviews.length > 0
    ? (approvedReviews.reduce((sum, r) => sum + r.rating, 0) / approvedReviews.length).toFixed(1)
    : null;

  // Stats by status
  const statsByStatus = {
    new: submissions.filter(s => !s.status || s.status === 'new').length,
    contacted: submissions.filter(s => s.status === 'contacted').length,
    scheduled: submissions.filter(s => s.status === 'scheduled').length,
    completed: submissions.filter(s => s.status === 'completed').length,
    archived: submissions.filter(s => s.status === 'archived').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="bg-[#003366] text-white py-6 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1">Admin Dashboard</h1>
              <p className="text-white/80 text-sm sm:text-base">Valenrock™ Home Services CRM</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => { fetchSubmissions(); fetchReviews(); }}
                disabled={isLoading}
                size="sm"
                className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] text-sm"
              >
                <RefreshCw className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} size={16} />
                Refresh
              </Button>
              <Button
                onClick={exportToCSV}
                variant="outline"
                size="sm"
                className="border-white text-white hover:bg-white hover:text-[#003366] text-sm"
              >
                <Download className="mr-1" size={16} />
                Export CSV
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-white text-white hover:bg-white hover:text-[#003366] text-sm"
              >
                <LogOut className="mr-1" size={16} />
                Logout
              </Button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-4 mt-6">
            <button
              onClick={() => setActiveTab('leads')}
              className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                activeTab === 'leads' 
                  ? 'bg-white text-[#003366]' 
                  : 'bg-[#003366]/50 text-white hover:bg-[#003366]/70'
              }`}
            >
              Leads ({submissions.length})
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`px-4 py-2 rounded-t-lg font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'reviews' 
                  ? 'bg-white text-[#003366]' 
                  : 'bg-[#003366]/50 text-white hover:bg-[#003366]/70'
              }`}
            >
              <Star size={16} />
              Reviews ({reviews.filter(r => r.status === 'pending').length} pending)
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'leads' ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                const Icon = config.icon;
                return (
                  <Card 
                    key={status}
                    className={`cursor-pointer transition-all ${filterStatus === status ? 'ring-2 ring-[#003366]' : 'hover:shadow-md'}`}
                    onClick={() => setFilterStatus(filterStatus === status ? 'all' : status)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">{config.label}</p>
                          <p className="text-2xl font-bold text-[#003366]">{statsByStatus[status]}</p>
                        </div>
                        <Icon className={`${config.color.split(' ')[1]}`} size={24} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Filter indicator */}
            {(filterStatus !== 'all' || searchQuery) && (
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <Filter size={16} className="text-gray-500" />
                {filterStatus !== 'all' && (
                  <span className="text-sm text-gray-600">
                    Status: <span className="font-medium">{STATUS_CONFIG[filterStatus]?.label}</span>
                  </span>
                )}
                {searchQuery && (
                  <span className="text-sm text-gray-600">
                    Search: <span className="font-medium">"{searchQuery}"</span>
                  </span>
                )}
                <button 
                  onClick={() => { setFilterStatus('all'); setSearchQuery(''); }}
                  className="text-sm text-[#003366] hover:underline flex items-center gap-1"
                >
                  <X size={14} /> Clear all
                </button>
                <span className="text-sm text-gray-400 ml-auto">{filteredSubmissions.length} result{filteredSubmissions.length !== 1 ? 's' : ''}</span>
              </div>
            )}

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads by name, email, phone, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/20 focus:border-[#003366]"
                  data-testid="lead-search-input"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Leads</p>
                  <p className="text-3xl font-bold text-[#003366]">{submissions.length}</p>
                </div>
                <Mail className="text-[#003366]" size={28} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">This Week</p>
                  <p className="text-3xl font-bold text-[#003366]">
                    {submissions.filter(sub => {
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return new Date(sub.submittedAt) > weekAgo;
                    }).length}
                  </p>
                </div>
                <Calendar className="text-[#CFB973]" size={28} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="text-lg font-semibold text-[#003366]">
                    {lastRefresh.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
                <Clock className="text-gray-400" size={28} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submissions List */}
        <div className="space-y-4">
          {isLoading ? (
            <Card>
              <CardContent className="p-12 text-center">
                <RefreshCw className="animate-spin mx-auto mb-4 text-[#003366]" size={32} />
                <p className="text-gray-600">Loading leads...</p>
              </CardContent>
            </Card>
          ) : filteredSubmissions.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Mail className="mx-auto mb-4 text-gray-400" size={48} />
                <p className="text-gray-600 text-lg">No leads found</p>
                <p className="text-gray-500 text-sm mt-2">
                  {filterStatus !== 'all' ? 'Try clearing the filter' : 'Contact form submissions will appear here'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredSubmissions.map((submission) => {
              const status = submission.status || 'new';
              const StatusIcon = STATUS_CONFIG[status]?.icon || Circle;
              const isExpanded = expandedCard === submission.id;
              const currentNotes = editingNotes[submission.id] !== undefined 
                ? editingNotes[submission.id] 
                : submission.notes || '';

              return (
                <Card key={submission.id} className="border-2 border-gray-200 hover:border-[#CFB973] transition-all">
                  <CardHeader className="bg-gray-50 border-b py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${STATUS_CONFIG[status]?.color}`}>
                          <StatusIcon size={12} />
                          {STATUS_CONFIG[status]?.label}
                        </div>
                        <CardTitle className="text-xl text-[#003366]">{submission.name}</CardTitle>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{formatDate(submission.submittedAt)}</span>
                        <Button
                          onClick={() => setExpandedCard(isExpanded ? null : submission.id)}
                          variant="outline"
                          size="sm"
                          className="text-[#003366] border-[#003366]"
                        >
                          {isExpanded ? 'Collapse' : 'Expand'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Contact Info Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <Mail className="text-[#003366] flex-shrink-0" size={18} />
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <a href={`mailto:${submission.email}`} className="text-[#003366] hover:underline text-sm">
                            {submission.email}
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="text-[#003366] flex-shrink-0" size={18} />
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <a href={`tel:${submission.phone}`} className="text-[#003366] hover:underline text-sm">
                            {submission.phone}
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPin className="text-[#003366] flex-shrink-0" size={18} />
                        <div>
                          <p className="text-xs text-gray-500">Location</p>
                          <p className="text-gray-700 text-sm">{submission.location}</p>
                        </div>
                      </div>
                    </div>

                    {/* Service Type & Interests */}
                    {(submission.serviceType || (submission.interests && submission.interests.length > 0)) && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        {submission.serviceType && (
                          <span className="px-3 py-1 bg-[#003366]/10 text-[#003366] rounded-full text-xs font-medium">
                            {submission.serviceType}
                          </span>
                        )}
                        {submission.interests?.map((interest, idx) => (
                          <span key={idx} className="px-3 py-1 bg-[#CFB973]/20 text-[#003366] rounded-full text-xs">
                            {interest === 'ongoing-care' && 'Ongoing Home Care'}
                            {interest === 'project' && 'Project/Renovation'}
                            {interest === 'consultation' && 'General Consultation'}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Message Preview */}
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-1">Message</p>
                      <p className="text-gray-700 text-sm bg-gray-50 p-3 rounded-lg line-clamp-2">
                        {submission.message}
                      </p>
                    </div>

                    {/* Tags */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag size={14} className="text-gray-500" />
                        <span className="text-xs text-gray-500">Tags</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(submission.tags || []).map(tag => (
                          <span 
                            key={tag}
                            onClick={() => handleTagToggle(submission.id, tag, submission.tags)}
                            className="px-2 py-1 bg-[#CFB973] text-[#003366] rounded text-xs font-medium cursor-pointer hover:bg-[#CFB973]/80"
                          >
                            {tag} ×
                          </span>
                        ))}
                        {isExpanded && TAG_OPTIONS.filter(t => !(submission.tags || []).includes(t)).map(tag => (
                          <span 
                            key={tag}
                            onClick={() => handleTagToggle(submission.id, tag, submission.tags)}
                            className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs cursor-pointer hover:bg-gray-200"
                          >
                            + {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="border-t pt-4 mt-4 space-y-4">
                        {/* Status Change */}
                        <div>
                          <p className="text-xs text-gray-500 mb-2">Update Status</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(STATUS_CONFIG).map(([statusKey, config]) => {
                              const Icon = config.icon;
                              return (
                                <button
                                  key={statusKey}
                                  onClick={() => handleStatusChange(submission.id, statusKey)}
                                  className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-all ${
                                    status === statusKey 
                                      ? config.color + ' ring-2 ring-offset-1 ring-gray-400' 
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  <Icon size={12} />
                                  {config.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Notes */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <MessageSquare size={14} className="text-gray-500" />
                              <span className="text-xs text-gray-500">Notes</span>
                            </div>
                            {editingNotes[submission.id] !== undefined && (
                              <Button
                                onClick={() => saveNotes(submission.id)}
                                size="sm"
                                className="bg-[#003366] hover:bg-[#003366]/90 text-white text-xs h-7"
                              >
                                <Save size={12} className="mr-1" />
                                Save Notes
                              </Button>
                            )}
                          </div>
                          <textarea
                            value={currentNotes}
                            onChange={(e) => handleNotesChange(submission.id, e.target.value)}
                            placeholder="Add notes about this lead..."
                            className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#003366]/20 focus:border-[#003366]"
                            rows={3}
                          />
                        </div>

                        {/* Additional Details */}
                        {submission.ownershipLength && (
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar size={14} className="text-gray-500" />
                            <span className="text-gray-500">Ownership:</span>
                            <span className="text-gray-700">{submission.ownershipLength}</span>
                          </div>
                        )}

                        {/* Full Message */}
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Full Message</p>
                          <p className="text-gray-700 text-sm bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                            {submission.message}
                          </p>
                        </div>

                        {/* Delete Button */}
                        <div className="flex justify-end pt-2">
                          <Button
                            onClick={() => handleDelete(submission.id, submission.name)}
                            variant="outline"
                            size="sm"
                            className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                          >
                            <Trash2 size={14} className="mr-1" />
                            Delete Lead
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
          </>
        ) : (
          /* Reviews Tab */
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#003366]">Review Management</h2>
              {averageRating && (
                <div className="flex items-center gap-2 bg-[#003366] text-white px-4 py-2 rounded-lg">
                  <Star size={18} className="text-[#CFB973] fill-[#CFB973]" />
                  <span className="text-xl font-bold">{averageRating}</span>
                  <span className="text-white/70 text-sm">/ 5.0 ({approvedReviews.length} review{approvedReviews.length !== 1 ? 's' : ''})</span>
                </div>
              )}
            </div>
            
            {/* Review Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <Card className="bg-yellow-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-600">{reviews.filter(r => r.status === 'pending').length}</p>
                  <p className="text-sm text-yellow-700">Pending</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{reviews.filter(r => r.status === 'approved').length}</p>
                  <p className="text-sm text-green-700">Approved</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{reviews.filter(r => r.status === 'rejected').length}</p>
                  <p className="text-sm text-red-700">Rejected</p>
                </CardContent>
              </Card>
            </div>

            {/* Reviews List */}
            <div className="space-y-4">
              {reviews.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-gray-500">
                    <Star className="mx-auto mb-4 text-gray-300" size={48} />
                    <p>No reviews yet</p>
                  </CardContent>
                </Card>
              ) : (
                reviews.map((review) => (
                  <Card key={review.id} className="overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-[#003366] flex items-center justify-center text-white font-bold">
                              {review.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            <div>
                              <p className="font-bold text-[#003366]">{review.name}</p>
                              <p className="text-xs text-gray-500">{formatDate(review.submittedAt)}</p>
                            </div>
                          </div>
                          <div className="flex mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                size={16} 
                                className={i < review.rating ? 'text-[#CFB973] fill-[#CFB973]' : 'text-gray-300'} 
                              />
                            ))}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${REVIEW_STATUS_CONFIG[review.status]?.color}`}>
                          {REVIEW_STATUS_CONFIG[review.status]?.label}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 italic mb-4">"{review.text}"</p>
                      
                      <div className="flex gap-2">
                        {review.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleReviewStatus(review.id, 'approved')}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle2 size={14} className="mr-1" />
                              Approve
                            </Button>
                            <Button
                              onClick={() => handleReviewStatus(review.id, 'rejected')}
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <X size={14} className="mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {review.status === 'rejected' && (
                          <Button
                            onClick={() => handleReviewStatus(review.id, 'approved')}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle2 size={14} className="mr-1" />
                            Approve
                          </Button>
                        )}
                        {review.status === 'approved' && (
                          <Button
                            onClick={() => handleReviewStatus(review.id, 'rejected')}
                            size="sm"
                            variant="outline"
                            className="border-yellow-300 text-yellow-600 hover:bg-yellow-50"
                          >
                            Hide
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDeleteReview(review.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
